import { pathOf, type Container } from "@egalregal/shared";
import type { OwnCatalogEntry, SharedCatalogEntry } from "../../shared/api/requests";
import type { MessageKey, Translate } from "../../shared/i18n";
import type { BackTarget } from "./back";
import type { RouteInfo } from "./routes";

/** One entry in the content-header crumb strip. `id: null` is the root. */
export interface Crumb {
  id: string | null;
  label: string;
}

/**
 * What the shell paints above the routed view. `back` is the resolved back
 * contract target (spec §6); `crumbs` is null for views without a trail.
 * `caption` is the quiet second line under a destination title — the Browse
 * root uses it for “by {owner}” on a shared catalog (catalog-selector 02).
 */
export interface ContentHeaderModel {
  title: string;
  back: BackTarget | null;
  crumbs: Crumb[] | null;
  caption?: string;
  /**
   * Container views only: the kind chip that sits at the right edge of the
   * title row, exactly like a tree row's trailing chip. The edit form keeps its
   * own kind field, so it never asks for this.
   */
  kind?: string;
  /**
   * Container views only: the container's owner, rendered as an outlined chip
   * before the kind chip so it reads at a glance. Omitted when unset (or blank).
   */
  owner?: string;
  /**
   * Container views only: the identity line (pictograms, short UUID, copy, QR)
   * painted under the crumb strip (uuid-icons-and-labels 03).
   */
  identity?: { uuid: string; name: string };
}

export interface ContentHeaderInput {
  /** The router pathname; ids are read from it, never from store navigation. */
  pathname: string;
  route: RouteInfo;
  /** The resolved back target for this view. */
  backTarget: BackTarget;
  /** The visible container union, for titles and crumb trails. */
  containers: readonly Container[];
  /** Own catalog entries; the Browse root title is the current one. */
  entries?: readonly OwnCatalogEntry[];
  /** Catalogs shared with the caller, for the owner caption. */
  sharedWithMe?: readonly SharedCatalogEntry[];
  /** The catalog the Browse root is scoped to. */
  selectedEntryId?: string;
  /** Translation lookup; the resolver stays pure for tests. */
  t: Translate;
}

/**
 * Derive the content header from the route predicate (spec §3). The shell
 * renders every header except the ones a screen owns (`headerOwner:
 * "screen"`, item detail), which renders the same `ContentHeader` component
 * with the item it is fetching.
 *
 * A missing container (deep link before the store loads, or a deleted id)
 * falls back to the view's label so the header never collapses.
 *
 * @param input pathname, route info, back target, containers and translation.
 * @returns the header model, or null when the route renders no shell header.
 */
export function resolveContentHeader(input: ContentHeaderInput): ContentHeaderModel | null {
  const { pathname, route, backTarget, containers, t } = input;
  if (route.header === "none" || route.headerOwner === "screen") return null;

  const segments = pathname.split("/").filter((segment) => segment.length > 0);
  const [head, id] = segments;

  if (route.header === "title") {
    return destinationHeader(head, input);
  }
  if (route.header === "task") {
    return { title: titleOf(TASK_TITLES, head, "addTitle", t), back: backTarget, crumbs: null };
  }

  const container = id ? containers.find((candidate) => candidate.id === id) : undefined;
  // Browse's container page is the container view: it wears the kind chip
  // beside the title and the identity line under the crumbs. Edit keeps its own
  // kind field, so it gets neither.
  const showsContainerMeta = container !== undefined && head === "browse";
  return {
    title: container?.name ?? titleOf(CONTAINER_FALLBACK_TITLES, head, "browseTitle", t),
    back: backTarget,
    crumbs: container ? trailOf(containers, container, t) : null,
    ...(showsContainerMeta
      ? {
          kind: container.kind,
          ...(container.owner?.trim() ? { owner: container.owner.trim() } : {}),
          identity: { uuid: container.id, name: container.name },
        }
      : {}),
  };
}

/**
 * A destination header: title only, no back, no crumbs. The Browse root's
 * title is the current catalog (with an owner caption when it is shared); a
 * catalog that has not loaded yet keeps the storage label (catalog-selector
 * 02). Every other destination keeps its static label, so the catalog context
 * never leaks into Items, Photos or the container views.
 */
function destinationHeader(
  head: string | undefined,
  { entries = [], sharedWithMe = [], selectedEntryId, t }: ContentHeaderInput,
): ContentHeaderModel {
  if (head === "browse") {
    const own = entries.find((entry) => entry.id === selectedEntryId);
    if (own) return { title: own.name, back: null, crumbs: null };
    const shared = sharedWithMe.find((candidate) => candidate.entry.id === selectedEntryId);
    if (shared) {
      return {
        title: shared.entry.name,
        back: null,
        crumbs: null,
        caption: t("sharedBy", { owner: shared.owner }),
      };
    }
  }
  return { title: titleOf(DESTINATION_TITLES, head, "homeTitle", t), back: null, crumbs: null };
}

/** Title-only destinations (spec §3); Browse's fallback is the storage root. */
const DESTINATION_TITLES: Record<string, MessageKey> = {
  catalogs: "catalogs",
  items: "sidebarItems",
  gallery: "gallery",
  browse: "homeTitle",
};

/** Add/Print/Trash titles; every task keeps an in-app exit. */
const TASK_TITLES: Record<string, MessageKey> = {
  add: "addTitle",
  print: "print",
  trash: "trash",
};

/** A container the store has not loaded yet still gets a sensible title. */
const CONTAINER_FALLBACK_TITLES: Record<string, MessageKey> = {
  browse: "browseTitle",
  edit: "editTitle",
};

function titleOf(
  titles: Record<string, MessageKey>,
  head: string | undefined,
  fallback: MessageKey,
  t: Translate,
): string {
  return t((head && titles[head]) || fallback);
}

/** Root crumb + every ancestor + the current container (spec §3). */
function trailOf(
  containers: readonly Container[],
  container: Container,
  t: Translate,
): Crumb[] {
  return [
    { id: null, label: t("allStorage") },
    ...pathOf(containers, container.id).map((node) => ({ id: node.id, label: node.name })),
  ];
}
